"use client";

import type { ReactNode } from "react";
import { useState } from "react";
import { FaClock, FaLocationDot, FaStar, FaXmark } from "react-icons/fa6";
import { ghostAccentClassNames, ghostCardClassNames } from "./ghostClassNames";
import type { GhostCta } from "./types";
import PrimaryCTAButton from "./PrimaryCTAButton";
import Eyebrow from "./Eyebrow";
import CardTitle from "./CardTitle";
import TextBlock from "./TextBlock";

export interface GhostStickyBookingBarProps {
  eyebrow: string;
  title: string;
  duration: string;
  meetingPoint: string;
  rating: string;
  cta: GhostCta;
}

function MetaChip({
  term,
  icon,
  label,
}: {
  term: string;
  icon: ReactNode;
  label: string;
}) {
  return (
    <li className={ghostAccentClassNames.metaChip}>
      <span className="text-ghost-gold" aria-hidden="true">
        {icon}
      </span>
      <span className="sr-only">{term}: </span>
      <TextBlock as="span" variant="extra small" tone="inherit">
        {label}
      </TextBlock>
    </li>
  );
}

export default function GhostStickyBookingBar({
  eyebrow,
  title,
  duration,
  meetingPoint,
  rating,
  cta,
}: GhostStickyBookingBarProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const hideBar = () => setIsDismissed(true);
  const metaItems = [
    {
      term: "Duration",
      icon: <FaClock size={10} aria-hidden="true" />,
      label: duration,
    },
    {
      term: "Meeting point",
      icon: <FaLocationDot size={10} aria-hidden="true" />,
      label: meetingPoint,
    },
    {
      term: "Rating",
      icon: <FaStar size={10} aria-hidden="true" />,
      label: rating,
    },
  ];

  if (isDismissed) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] md:hidden">
      <div className={ghostCardClassNames.stickyShell}>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,200,57,0.16),transparent_42%),radial-gradient(circle_at_80%_15%,rgba(187,37,29,0.22),transparent_30%)]"
        />
        <div className="relative p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col">
              <Eyebrow text={eyebrow} as="p" />
              <CardTitle
                title={title}
                as="p"
                variant="large"
                className="pb-4 !leading-7"
              />
            </div>

            <button
              type="button"
              onClick={hideBar}
              aria-label="Dismiss booking bar"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-black/30 text-zinc-300 transition-colors hover:border-white/25 hover:text-ghost-gold"
            >
              <FaXmark size={14} aria-hidden="true" />
            </button>
          </div>

          <ul className="mt-3 flex flex-wrap gap-2">
            {metaItems.map((item) => (
              <MetaChip
                key={`${item.label}`}
                icon={item.icon}
                label={item.label}
                term={item.term}
              />
            ))}
          </ul>

          <PrimaryCTAButton
            href={cta.href}
            label={cta.label}
            internal={false}
            className="mt-6 w-full px-4 text-[11px]"
            variant="red"
          />
        </div>
      </div>
    </div>
  );
}
